import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles, Crown, CheckCircle, Star, Target, Shield, Heart, Briefcase, TrendingUp, Zap } from 'lucide-react';

const gold = '#C9A961';

const GOALS = [
  { id: 'career', label: 'Career Growth', icon: Briefcase, color: '#3B82F6' },
  { id: 'money', label: 'Wealth & Money', icon: TrendingUp, color: '#10B981' },
  { id: 'fame', label: 'Fame & Recognition', icon: Star, color: '#F59E0B' },
  { id: 'stability', label: 'Life Stability', icon: Shield, color: '#8B5CF6' },
  { id: 'love', label: 'Love & Relationships', icon: Heart, color: '#EC4899' },
  { id: 'spiritual', label: 'Spiritual Path', icon: Zap, color: '#06B6D4' },
];

function NameCorrection() {
  const [currentName, setCurrentName] = useState('');
  const [goal, setGoal] = useState('career');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selectedName, setSelectedName] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Load profile name
    axios.get('/api/profile').then(r => {
      if (r.data.exists) setCurrentName(r.data.profile.full_name || '');
    }).catch(() => {});
    // Check if already has a corrected name
    axios.get('/api/numerology/corrected-name').then(r => {
      if (r.data.corrected_name) setSelectedName(r.data);
    }).catch(() => {});
  }, []);

  const handleAnalyze = async () => {
    if (!currentName) return;
    setLoading(true);
    try {
      const res = await axios.post('/api/numerology/name-corrections', { current_name: currentName, goal });
      setResult(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSelectName = async (suggestion) => {
    setSelecting(true);
    try {
      await axios.post('/api/numerology/select-corrected-name', {
        original_name: currentName,
        corrected_name: suggestion.name,
        compound_number: suggestion.compound_number,
        single_number: suggestion.single_number,
        compound_info: suggestion.compound_info,
        reasons: suggestion.reasons,
        goal,
      });
      setSelectedName({
        corrected_name: suggestion.name,
        compound_number: suggestion.compound_number,
        compound_info: suggestion.compound_info,
        reasons: suggestion.reasons,
      });
      navigate('/results');
    } catch (err) { console.error(err); }
    finally { setSelecting(false); }
  };

  const compoundColor = (quality) => {
    if (quality === 'Excellent') return '#10B981';
    if (quality === 'Very Good' || quality === 'Good') return '#F59E0B';
    if (quality === 'Avoid') return '#EF4444';
    return '#9CA3AF';
  };

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '850px' }}>
        <button onClick={() => navigate('/dashboard')} className="btn btn-ghost" style={{ marginBottom: '1.5rem' }}>
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} /> Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>Name Correction Engine</h1>
          <p style={{ color: '#9CA3AF', marginBottom: '1.5rem' }}>Professional name alignment using compound number science</p>
        </motion.div>

        {/* Already selected name banner */}
        {selectedName && (
          <div data-testid="selected-name-banner" style={{ marginBottom: '1.5rem', padding: '1.25rem', borderRadius: '14px', background: 'rgba(16,185,129,0.08)', border: '2px solid rgba(16,185,129,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
              <Crown size={20} color="#10B981" />
              <span style={{ color: '#10B981', fontWeight: 700, fontSize: '0.85rem' }}>YOUR CORRECTED NAME</span>
            </div>
            <p style={{ color: '#FFF', fontSize: '1.5rem', fontWeight: 700 }}>{selectedName.corrected_name}</p>
            <p style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>
              Compound {selectedName.compound_number} — {selectedName.compound_info?.name}
            </p>
          </div>
        )}

        {/* Input Section */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label className="form-label">Your Name</label>
            <input type="text" className="form-input" value={currentName} onChange={(e) => setCurrentName(e.target.value)}
              placeholder="Enter your full name" data-testid="current-name-input" />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label">What's your primary goal?</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
              {GOALS.map(g => {
                const Icon = g.icon;
                const active = goal === g.id;
                return (
                  <button key={g.id} data-testid={`goal-${g.id}`} onClick={() => setGoal(g.id)}
                    style={{
                      padding: '0.7rem 0.5rem', borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
                      background: active ? `${g.color}15` : 'rgba(255,255,255,0.03)',
                      border: active ? `2px solid ${g.color}` : '1px solid rgba(255,255,255,0.08)',
                      color: active ? g.color : '#6B7280', fontSize: '0.8rem', fontWeight: active ? 700 : 400,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem',
                    }}>
                    <Icon size={18} /> {g.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button onClick={handleAnalyze} className="btn btn-primary" disabled={loading || !currentName}
            data-testid="analyze-button" style={{ width: '100%', fontSize: '1rem', padding: '0.85rem' }}>
            {loading ? 'Analyzing with Compound Number Science...' : 'Analyze & Get Corrections'}
          </button>
        </div>

        {/* Results */}
        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              {/* Current Name Analysis */}
              <div style={{ marginBottom: '1.25rem', padding: '1.25rem', borderRadius: '14px', background: '#0F172A', border: '1px solid rgba(255,255,255,0.08)' }}>
                <h3 style={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.75rem', textTransform: 'uppercase' }}>Current Name Analysis</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                  <div style={{ padding: '0.5rem 1rem', borderRadius: '10px', background: `${compoundColor(result.current_analysis.compound_info.quality)}15`, border: `1px solid ${compoundColor(result.current_analysis.compound_info.quality)}40` }}>
                    <span style={{ color: compoundColor(result.current_analysis.compound_info.quality), fontWeight: 700, fontSize: '1.2rem' }}>
                      {result.current_analysis.compound_number}
                    </span>
                  </div>
                  <div>
                    <p style={{ color: '#E5E7EB', fontWeight: 600 }}>{result.current_analysis.name} = Compound {result.current_analysis.compound_number}</p>
                    <p style={{ color: compoundColor(result.current_analysis.compound_info.quality), fontSize: '0.85rem' }}>
                      {result.current_analysis.compound_info.quality} — {result.current_analysis.compound_info.effect}
                    </p>
                  </div>
                </div>
                {result.current_analysis.issues.map((issue, i) => (
                  <p key={i} style={{ color: '#F59E0B', fontSize: '0.85rem', marginBottom: '0.2rem' }}>{issue}</p>
                ))}
                <p style={{ color: '#6B7280', fontSize: '0.8rem', marginTop: '0.4rem' }}>
                  Driver: {result.birth_analysis.driver} | Conductor: {result.birth_analysis.conductor} | Friendly: {result.birth_analysis.friendly_numbers.join(', ')} | {result.current_analysis.vowel_consonant_balance}
                </p>
              </div>

              {/* Keep Current Name Option */}
              {result.keep_current && (
                <div style={{ marginBottom: '1rem', padding: '1rem', borderRadius: '12px', background: 'rgba(16,185,129,0.06)', border: '2px solid rgba(16,185,129,0.3)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ color: '#10B981', fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.2rem' }}>Your current name is already strong!</p>
                      <p style={{ color: '#9CA3AF', fontSize: '0.82rem' }}>Compound {result.keep_current.compound_number} is a {result.keep_current.compound_info.quality} number — you can keep it as is.</p>
                    </div>
                    <button onClick={() => handleSelectName(result.keep_current)} disabled={selecting}
                      style={{ background: '#10B981', color: '#FFF', padding: '0.6rem 1.2rem', borderRadius: '10px', fontWeight: 700, fontSize: '0.82rem', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {selecting ? '...' : 'Keep Current Name'}
                    </button>
                  </div>
                </div>
              )}

              {/* Suggestions */}
              <h2 style={{ fontSize: '1.25rem', color: '#FFF', marginBottom: '0.75rem' }}>
                Recommended Names for {GOALS.find(g => g.id === goal)?.label}
              </h2>

              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {result.suggestions.map((sug, idx) => (
                  <motion.div key={idx} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.08 }}
                    style={{
                      padding: '1.25rem', borderRadius: '14px', background: '#0F172A',
                      border: idx === 0 ? `2px solid ${gold}` : '1px solid rgba(255,255,255,0.08)',
                      position: 'relative',
                    }}>
                    {idx === 0 && (
                      <div style={{ position: 'absolute', top: '-10px', right: '16px', background: gold, color: '#0A0F2A', padding: '0.2rem 0.8rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700 }}>BEST MATCH</div>
                    )}
                    {sug.method === 'parental_initial' && (
                      <div style={{ position: 'absolute', top: '-10px', left: '16px', background: '#8B5CF6', color: '#FFF', padding: '0.2rem 0.8rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700 }}>PARENTAL LINEAGE</div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ color: '#FFF', fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.3rem' }}>{sug.name}</p>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', borderRadius: '6px', background: `${compoundColor(sug.compound_info.quality)}15`, color: compoundColor(sug.compound_info.quality), fontWeight: 700 }}>
                            Compound {sug.compound_number} — {sug.compound_info.name}
                          </span>
                          <span style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', borderRadius: '6px', background: 'rgba(59,130,246,0.15)', color: '#3B82F6' }}>
                            = {sug.single_number}
                          </span>
                          <span style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', borderRadius: '6px', background: 'rgba(201,169,97,0.15)', color: gold }}>
                            Score: {sug.score}
                          </span>
                        </div>
                        <p style={{ color: '#9CA3AF', fontSize: '0.82rem', marginBottom: '0.3rem' }}>{sug.compound_info.effect}</p>
                        <p style={{ color: '#6B7280', fontSize: '0.78rem' }}>Change: {sug.spelling_change}</p>
                        <div style={{ marginTop: '0.4rem' }}>
                          {sug.reasons.map((r, ri) => (
                            <span key={ri} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.72rem', color: '#10B981', marginRight: '0.75rem' }}>
                              <CheckCircle size={11} /> {r}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => handleSelectName(sug)} disabled={selecting}
                        data-testid={`select-name-${idx}`}
                        style={{
                          padding: '0.6rem 1.2rem', borderRadius: '10px', cursor: 'pointer',
                          background: idx === 0 ? gold : 'rgba(201,169,97,0.15)',
                          color: idx === 0 ? '#0A0F2A' : gold,
                          border: idx === 0 ? 'none' : `1px solid ${gold}`,
                          fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap',
                        }}>
                        {selecting ? '...' : 'Select This'}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default NameCorrection;
